import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { channelPartnerAdminAPI, filePublicUrl } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlinePlus,
  HiOutlineX,
  HiOutlineUserGroup,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineCurrencyRupee,
  HiOutlineCash,
  HiOutlineDocumentText,
  HiOutlineClipboardCopy,
  HiOutlineLightningBolt,
  HiOutlineMail,
  HiOutlineAnnotation,
  HiOutlineUserAdd,
  HiOutlineShieldCheck,
  HiOutlineCheck,
  HiOutlineDocumentDownload,
  HiOutlineCalendar,
  HiOutlinePhone,
} from 'react-icons/hi';
import { buildPageList } from '../../utils/buildPageList';
import { ExportMenu, DateRangeFilter, FilterSelect, ResetFiltersButton } from '../../components/SuperAdmin/ListTools';
import { fetchAllPages, dateRangeParams, formatExportDate } from '../../utils/exportData';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// ── Per-tab filter defaults (page / limit are kept separately in each tab's state) ──
const EMPTY_PARTNER_FILTERS = {
  search: '', isActive: '', commissionType: '', commissionRecurrence: '', signupSource: '',
  autoPayout: '', kycStatus: '', from: '', to: '',
};
const EMPTY_ENQUIRY_FILTERS = { search: '', type: '', status: '', from: '', to: '' };
const EMPTY_KYC_FILTERS = { search: '', from: '', to: '' };
const EMPTY_MEETING_FILTERS = { search: '', status: '', channelPartnerId: '', from: '', to: '', preferredFrom: '', preferredTo: '' };

const hasActiveFilters = (filters, empty) => Object.keys(empty).some((k) => filters[k]);

/** Strip page/limit and turn local date ranges into ISO params for the API. */
const partnerApiParams = ({ from, to, preferredFrom, preferredTo, ...rest }) => {
  const preferred = dateRangeParams(preferredFrom, preferredTo);
  return {
    ...rest,
    ...dateRangeParams(from, to),
    ...(preferred.from && { preferredFrom: preferred.from }),
    ...(preferred.to && { preferredTo: preferred.to }),
  };
};

const KYC_STATUS_LABELS = {
  not_required: 'Not required',
  not_submitted: 'Not submitted',
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};

const PARTNER_EXPORT_COLUMNS = [
  { header: 'Name', value: (p) => p.name },
  { header: 'Email', value: (p) => p.email },
  { header: 'Phone', value: (p) => p.phone },
  { header: 'Token', value: (p) => p.token },
  { header: 'Commission Type', value: (p) => p.commissionType },
  { header: 'Commission Value', value: (p) => p.commissionValue },
  { header: 'Recurrence', value: (p) => (p.commissionRecurrence === 'recurring' ? 'Every payment' : 'First payment only') },
  { header: 'Autopay', value: (p) => (p.autoPayoutEnabled ? p.autoPayoutFrequency || 'immediate' : 'Off') },
  { header: 'Total Earned (₹)', value: (p) => p.totalEarned || 0 },
  { header: 'Total Paid (₹)', value: (p) => p.totalPaid || 0 },
  { header: 'Pending (₹)', value: (p) => (p.totalEarned || 0) - (p.totalPaid || 0) },
  { header: 'Status', value: (p) => (p.isActive ? 'Active' : 'Inactive') },
  { header: 'Signup Source', value: (p) => (p.signupSource === 'self_signup' ? 'Self signup' : 'Created by SuperAdmin') },
  { header: 'KYC Status', value: (p) => KYC_STATUS_LABELS[p.kycStatus] || p.kycStatus },
  { header: 'Country', value: (p) => p.country },
  { header: 'Payout Method', value: (p) => p.payout?.method },
  { header: 'Created', value: (p) => formatExportDate(p.createdAt) },
];

const ENQUIRY_EXPORT_COLUMNS = [
  { header: 'Type', value: (e) => (e.type === 'affiliate' ? 'Affiliate' : 'Query') },
  { header: 'Name', value: (e) => e.fullName },
  { header: 'Email', value: (e) => e.email },
  { header: 'Phone', value: (e) => e.phone },
  { header: 'Company', value: (e) => e.company },
  { header: 'Message', value: (e) => e.message },
  { header: 'Status', value: (e) => e.status },
  { header: 'Notes', value: (e) => e.notes },
  { header: 'Submitted', value: (e) => formatExportDate(e.createdAt) },
];

const KYC_EXPORT_COLUMNS = [
  { header: 'Name', value: (p) => p.name },
  { header: 'Email', value: (p) => p.email },
  { header: 'Phone', value: (p) => p.phone },
  { header: 'Country', value: (p) => p.country },
  { header: 'Documents', value: (p) => p.kycDocuments?.length || 0 },
  { header: 'KYC Status', value: (p) => KYC_STATUS_LABELS[p.kycStatus] || p.kycStatus },
  { header: 'Submitted', value: (p) => formatExportDate(p.createdAt) },
];

const MEETING_EXPORT_COLUMNS = [
  { header: 'Partner', value: (r) => r.channelPartnerId?.name },
  { header: 'Partner Email', value: (r) => r.channelPartnerId?.email },
  { header: 'Lead Name', value: (r) => r.leadName },
  { header: 'Lead Phone', value: (r) => r.leadPhone },
  { header: 'Lead Email', value: (r) => r.leadEmail },
  { header: 'Company', value: (r) => r.company },
  { header: 'Preferred Date', value: (r) => formatExportDate(r.preferredDate, false) },
  { header: 'Scheduled At', value: (r) => formatExportDate(r.scheduledAt) },
  { header: 'Status', value: (r) => r.status },
  { header: 'Partner Notes', value: (r) => r.notes },
  { header: 'SuperAdmin Notes', value: (r) => r.superadminNotes },
  { header: 'Requested', value: (r) => formatExportDate(r.createdAt) },
];

const copyPartnerLink = async (linkHash) => {
  const link = `${window.location.origin}/r/${linkHash}`;
  try {
    await navigator.clipboard.writeText(link);
    toast.success('Referral link copied to clipboard');
  } catch {
    toast.error('Failed to copy — please copy it manually');
  }
};

const defaultFormData = {
  name: '',
  email: '',
  phone: '',
  password: '',
  token: '',
  commissionType: 'percentage',
  commissionValue: 10,
  commissionRecurrence: 'first_payment_only',
  notes: '',
  isActive: true,
  // Payout details — required only for the automated "Pay via Razorpay" button;
  // manual "Mark Paid" works without these.
  payoutMethod: '',
  payoutAccountHolderName: '',
  payoutAccountNumber: '',
  payoutIfsc: '',
  payoutVpa: '',
  // Opt-in: auto-pay this partner's commissions via RazorpayX instead of
  // waiting for manual "Mark Paid" / "Pay via Razorpay". Still needs a
  // payout method above, and RazorpayX configured in Settings → Razorpay.
  autoPayoutEnabled: false,
  // How often THIS partner's autopay runs — different partners can be on
  // different cadences.
  autoPayoutFrequency: 'immediate',
};

const ChannelPartnersList = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('partners'); // 'partners' | 'enquiries' | 'kyc' | 'meetings'
  const [showModal, setShowModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({ ...defaultFormData });
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  // Set when the Create modal was opened via an enquiry's "Convert" button —
  // on successful creation, that enquiry's status flips to "approved".
  const [convertingEnquiryId, setConvertingEnquiryId] = useState(null);

  const [ledgerPartner, setLedgerPartner] = useState(null);
  const [payoutError, setPayoutError] = useState('');

  // Custom "Mark Paid" confirmation modal (replaces window.prompt)
  const [markPaidTarget, setMarkPaidTarget] = useState(null); // the commission row being marked
  const [markPaidReference, setMarkPaidReference] = useState('');
  const [markPaidError, setMarkPaidError] = useState('');

  // "View Note" modal — shows the full payoutReference/failureReason text for a commission
  const [noteModalTarget, setNoteModalTarget] = useState(null);

  // "View Message" modal — shows an enquiry's full message text
  const [enquiryMessageTarget, setEnquiryMessageTarget] = useState(null);

  const [partnerFilters, setPartnerFilters] = useState({ ...EMPTY_PARTNER_FILTERS, page: 1, limit: 20 });
  const setPartnerFilter = (patch) => setPartnerFilters((prev) => ({ ...prev, ...patch, page: 1 }));

  const { data: partnersData, isLoading } = useQuery({
    queryKey: ['superadmin-channel-partners', partnerFilters],
    queryFn: () => channelPartnerAdminAPI.getAll(partnerApiParams(partnerFilters)),
  });

  const partners = partnersData?.data?.data || [];
  const partnerPagination = partnersData?.data?.pagination || {};

  // ── Partner page enquiries ("Become an Affiliate" / "Any Query" submissions) ──
  const [enquiryFilters, setEnquiryFilters] = useState({ ...EMPTY_ENQUIRY_FILTERS, page: 1, limit: 20 });
  const setEnquiryFilter = (patch) => setEnquiryFilters((prev) => ({ ...prev, ...patch, page: 1 }));

  const { data: enquiriesData, isLoading: enquiriesLoading } = useQuery({
    queryKey: ['superadmin-partner-enquiries', enquiryFilters],
    queryFn: () => channelPartnerAdminAPI.getEnquiries(partnerApiParams(enquiryFilters)),
    enabled: activeTab === 'enquiries',
  });

  const enquiries = enquiriesData?.data?.data || [];
  const enquiryPagination = enquiriesData?.data?.pagination || {};

  // ── Self-signup KYC applications ──
  const [kycFilters, setKycFilters] = useState({ ...EMPTY_KYC_FILTERS, page: 1, limit: 20 });
  const setKycFilter = (patch) => setKycFilters((prev) => ({ ...prev, ...patch, page: 1 }));

  const { data: kycData, isLoading: kycLoading } = useQuery({
    queryKey: ['superadmin-channel-partners-kyc', kycFilters],
    queryFn: () => channelPartnerAdminAPI.getAll({ ...partnerApiParams(kycFilters), kycStatus: 'pending' }),
    enabled: activeTab === 'kyc',
  });

  const kycPartners = kycData?.data?.data || [];
  const kycPagination = kycData?.data?.pagination || {};
  const pendingKycCount = kycData?.data?.pendingKycCount || 0;
  const newEnquiryCount = enquiriesData?.data?.newCount || 0;

  const updateEnquiryMutation = useMutation({
    mutationFn: ({ id, data }) => channelPartnerAdminAPI.updateEnquiry(id, data),
    onSuccess: () => queryClient.invalidateQueries(['superadmin-partner-enquiries']),
    onError: (error) => toast.error(error.response?.data?.message || 'Failed to update enquiry'),
  });

  const deleteEnquiryMutation = useMutation({
    mutationFn: channelPartnerAdminAPI.deleteEnquiry,
    onSuccess: () => {
      queryClient.invalidateQueries(['superadmin-partner-enquiries']);
      toast.success('Enquiry deleted');
    },
    onError: (error) => toast.error(error.response?.data?.message || 'Failed to delete enquiry'),
  });

  const handleDeleteEnquiry = (id, name) => {
    if (window.confirm(`Delete the enquiry from "${name}"? This cannot be undone.`)) {
      deleteEnquiryMutation.mutate(id);
    }
  };

  // ── KYC review modal — approve (with commission + payout terms) or reject ──
  const [kycReviewTarget, setKycReviewTarget] = useState(null); // the applicant being reviewed
  const [kycReviewForm, setKycReviewForm] = useState(null);
  const [kycRejectionReason, setKycRejectionReason] = useState('');
  const [kycReviewError, setKycReviewError] = useState('');

  const openKycReview = (partner) => {
    setKycReviewTarget(partner);
    setKycReviewForm({
      commissionType: partner.commissionType || 'percentage',
      commissionValue: partner.commissionValue || 10,
      commissionRecurrence: partner.commissionRecurrence || 'first_payment_only',
    });
    setKycRejectionReason('');
    setKycReviewError('');
  };

  const closeKycReview = () => {
    setKycReviewTarget(null);
    setKycReviewForm(null);
    setKycRejectionReason('');
    setKycReviewError('');
  };

  const reviewKycMutation = useMutation({
    mutationFn: ({ id, data }) => channelPartnerAdminAPI.reviewKyc(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['superadmin-channel-partners-kyc']);
      queryClient.invalidateQueries(['superadmin-channel-partners']);
      toast.success('KYC application reviewed');
      closeKycReview();
    },
    onError: (error) => setKycReviewError(error.response?.data?.message || 'Failed to review KYC application'),
  });

  const submitKycApprove = (e) => {
    e.preventDefault();
    setKycReviewError('');
    if (!(kycReviewForm.commissionValue > 0)) {
      setKycReviewError('Commission value must be greater than 0');
      return;
    }
    if (kycReviewForm.commissionType === 'percentage' && kycReviewForm.commissionValue > 100) {
      setKycReviewError('Percentage commission cannot exceed 100%');
      return;
    }
    reviewKycMutation.mutate({
      id: kycReviewTarget._id,
      data: {
        status: 'approved',
        commissionType: kycReviewForm.commissionType,
        commissionValue: Number(kycReviewForm.commissionValue),
        commissionRecurrence: kycReviewForm.commissionRecurrence,
      },
    });
  };

  const submitKycReject = () => {
    if (!window.confirm(`Reject ${kycReviewTarget.name}'s KYC application?`)) return;
    reviewKycMutation.mutate({
      id: kycReviewTarget._id,
      data: { status: 'rejected', rejectionReason: kycRejectionReason.trim() || undefined },
    });
  };

  const { data: ledgerData, isLoading: ledgerLoading } = useQuery({
    queryKey: ['superadmin-channel-partner-commissions', ledgerPartner?._id],
    queryFn: () => channelPartnerAdminAPI.getCommissions(ledgerPartner._id, { limit: 100 }),
    enabled: !!ledgerPartner,
  });

  const commissions = ledgerData?.data?.data || [];
  const totals = ledgerData?.data?.totals || { pending: 0, paid: 0, failed: 0 };

  // ── "Message" modal — compose box + history, sent to one partner's Notice Board ──
  const [messageTarget, setMessageTarget] = useState(null); // the partner being messaged
  const [messageText, setMessageText] = useState('');
  const [messageExpiresAt, setMessageExpiresAt] = useState('');

  const openMessageModal = (partner) => {
    setMessageTarget(partner);
    setMessageText('');
    setMessageExpiresAt('');
  };
  const closeMessageModal = () => {
    setMessageTarget(null);
    setMessageText('');
    setMessageExpiresAt('');
  };

  const { data: messageHistoryData, isLoading: messageHistoryLoading } = useQuery({
    queryKey: ['superadmin-channel-partner-messages', messageTarget?._id],
    queryFn: () => channelPartnerAdminAPI.getMessages(messageTarget._id),
    enabled: !!messageTarget,
  });
  const messageHistory = messageHistoryData?.data?.data || [];

  const sendMessageMutation = useMutation({
    mutationFn: ({ id, data }) => channelPartnerAdminAPI.sendMessage(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['superadmin-channel-partner-messages', messageTarget?._id]);
      setMessageText('');
      setMessageExpiresAt('');
      toast.success('Message sent');
    },
    onError: (error) => toast.error(error.response?.data?.message || 'Failed to send message'),
  });

  const submitMessage = (e) => {
    e.preventDefault();
    if (!messageText.trim()) return;
    sendMessageMutation.mutate({
      id: messageTarget._id,
      data: { message: messageText.trim(), expiresAt: messageExpiresAt || undefined },
    });
  };

  // ── Broadcast modal — message all partners, or a selected subset ──
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastMode, setBroadcastMode] = useState('all'); // 'all' | 'select'
  const [broadcastSelectedIds, setBroadcastSelectedIds] = useState([]);
  const [broadcastText, setBroadcastText] = useState('');
  const [broadcastExpiresAt, setBroadcastExpiresAt] = useState('');
  const [broadcastError, setBroadcastError] = useState('');

  const openBroadcastModal = () => {
    setShowBroadcastModal(true);
    setBroadcastMode('all');
    setBroadcastSelectedIds([]);
    setBroadcastText('');
    setBroadcastExpiresAt('');
    setBroadcastError('');
  };
  const closeBroadcastModal = () => setShowBroadcastModal(false);

  // Full partner list for the picker — independent of the (paginated) main table.
  const { data: broadcastPartnersData, isLoading: broadcastPartnersLoading } = useQuery({
    queryKey: ['superadmin-channel-partners-all-for-broadcast'],
    queryFn: () => channelPartnerAdminAPI.getAll({ limit: 1000, isActive: true }),
    enabled: showBroadcastModal && broadcastMode === 'select',
  });
  const broadcastablePartners = broadcastPartnersData?.data?.data || [];

  const toggleBroadcastSelected = (id) => {
    setBroadcastSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const broadcastMessageMutation = useMutation({
    mutationFn: (data) => channelPartnerAdminAPI.broadcastMessage(data),
    onSuccess: (response) => {
      toast.success(response.data?.message || 'Message sent');
      closeBroadcastModal();
    },
    onError: (error) => setBroadcastError(error.response?.data?.message || 'Failed to send message'),
  });

  const submitBroadcast = (e) => {
    e.preventDefault();
    setBroadcastError('');
    if (!broadcastText.trim()) {
      setBroadcastError('Message cannot be empty');
      return;
    }
    if (broadcastMode === 'select' && broadcastSelectedIds.length === 0) {
      setBroadcastError('Select at least one partner');
      return;
    }
    broadcastMessageMutation.mutate({
      message: broadcastText.trim(),
      sendToAll: broadcastMode === 'all',
      partnerIds: broadcastMode === 'select' ? broadcastSelectedIds : undefined,
      expiresAt: broadcastExpiresAt || undefined,
    });
  };

  // ── Partner meeting requests ──
  const [meetingFilters, setMeetingFilters] = useState({ ...EMPTY_MEETING_FILTERS, page: 1, limit: 20 });
  const setMeetingFilter = (patch) => setMeetingFilters((prev) => ({ ...prev, ...patch, page: 1 }));

  const { data: meetingsData, isLoading: meetingsLoading } = useQuery({
    queryKey: ['superadmin-partner-meeting-requests', meetingFilters],
    queryFn: () => channelPartnerAdminAPI.getPartnerMeetingRequests(partnerApiParams(meetingFilters)),
    enabled: activeTab === 'meetings',
  });

  // Partner options for the meeting-request filter (all partners, active or not)
  const { data: meetingPartnerOptions = [] } = useQuery({
    queryKey: ['superadmin-channel-partner-options'],
    queryFn: async () => (await channelPartnerAdminAPI.getAll({ limit: 500 })).data?.data || [],
    enabled: activeTab === 'meetings',
    staleTime: 5 * 60 * 1000,
  });

  const meetingRequests = meetingsData?.data?.data || [];
  const meetingPagination = meetingsData?.data?.pagination || {};
  const pendingMeetingCount = meetingsData?.data?.pendingCount || 0;

  const [meetingEditTarget, setMeetingEditTarget] = useState(null);
  const [meetingEditForm, setMeetingEditForm] = useState({ status: 'pending', scheduledAt: '', superadminNotes: '' });

  const openMeetingEdit = (reqItem) => {
    setMeetingEditTarget(reqItem);
    setMeetingEditForm({
      status: reqItem.status,
      scheduledAt: reqItem.scheduledAt ? new Date(reqItem.scheduledAt).toISOString().split('T')[0] : '',
      superadminNotes: reqItem.superadminNotes || '',
    });
  };

  const updateMeetingMutation = useMutation({
    mutationFn: ({ id, data }) => channelPartnerAdminAPI.updatePartnerMeetingRequest(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['superadmin-partner-meeting-requests']);
      toast.success('Meeting request updated');
      setMeetingEditTarget(null);
    },
    onError: (error) => toast.error(error.response?.data?.message || 'Failed to update meeting request'),
  });

  const submitMeetingEdit = (e) => {
    e.preventDefault();
    updateMeetingMutation.mutate({
      id: meetingEditTarget._id,
      data: {
        status: meetingEditForm.status,
        scheduledAt: meetingEditForm.scheduledAt || undefined,
        superadminNotes: meetingEditForm.superadminNotes,
      },
    });
  };

  const createMutation = useMutation({
    mutationFn: channelPartnerAdminAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['superadmin-channel-partners']);
      if (convertingEnquiryId) {
        updateEnquiryMutation.mutate({ id: convertingEnquiryId, data: { status: 'approved' } });
      }
      closeModal();
    },
    onError: (error) => setFormError(error.response?.data?.message || 'Failed to create channel partner'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => channelPartnerAdminAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['superadmin-channel-partners']);
      closeModal();
    },
    onError: (error) => setFormError(error.response?.data?.message || 'Failed to update channel partner'),
  });

  const deleteMutation = useMutation({
    mutationFn: channelPartnerAdminAPI.delete,
    onSuccess: () => queryClient.invalidateQueries(['superadmin-channel-partners']),
  });

  const markPaidMutation = useMutation({
    mutationFn: ({ commissionId, data }) => channelPartnerAdminAPI.markCommissionPaid(commissionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['superadmin-channel-partner-commissions']);
      queryClient.invalidateQueries(['superadmin-channel-partners']);
      closeMarkPaidModal();
    },
    onError: (error) => setMarkPaidError(error.response?.data?.message || 'Failed to mark as paid'),
  });

  const payoutMutation = useMutation({
    mutationFn: (commissionId) => channelPartnerAdminAPI.payoutCommission(commissionId),
    onSuccess: () => {
      setPayoutError('');
      queryClient.invalidateQueries(['superadmin-channel-partner-commissions']);
      queryClient.invalidateQueries(['superadmin-channel-partners']);
    },
    onError: (error) => setPayoutError(error.response?.data?.message || 'Payout failed'),
  });

  const closeModal = () => {
    setShowModal(false);
    setEditingPartner(null);
    setFormError('');
    setFormData({ ...defaultFormData });
    setConvertingEnquiryId(null);
  };

  const remainingOwed = (commission) =>
    Math.round(Math.max(commission.amount - (commission.status === 'paid' ? (commission.amountPaid || 0) : 0), 0) * 100) / 100;

  const openMarkPaidModal = (commission) => {
    setMarkPaidTarget(commission);
    setMarkPaidReference('');
    setMarkPaidError('');
  };

  const closeMarkPaidModal = () => {
    setMarkPaidTarget(null);
    setMarkPaidReference('');
    setMarkPaidError('');
  };

  // Full payment only — partial amounts are not allowed, so there's no
  // amount to enter; this always settles the entire remaining balance.
  const submitMarkPaid = (e) => {
    e.preventDefault();
    markPaidMutation.mutate({
      commissionId: markPaidTarget._id,
      data: { payoutReference: markPaidReference.trim() || undefined },
    });
  };

  const openEditModal = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name || '',
      email: partner.email || '',
      phone: partner.phone || '',
      password: '',
      token: partner.token || '',
      commissionType: partner.commissionType || 'percentage',
      commissionValue: partner.commissionValue || 0,
      commissionRecurrence: partner.commissionRecurrence || 'first_payment_only',
      notes: partner.notes || '',
      isActive: partner.isActive !== undefined ? partner.isActive : true,
      payoutMethod: partner.payout?.method || '',
      payoutAccountHolderName: partner.payout?.accountHolderName || '',
      payoutAccountNumber: partner.payout?.accountNumber || '',
      payoutIfsc: partner.payout?.ifsc || '',
      payoutVpa: partner.payout?.vpa || '',
      autoPayoutEnabled: !!partner.autoPayoutEnabled,
      autoPayoutFrequency: partner.autoPayoutFrequency || 'immediate',
    });
    setShowModal(true);
  };

  // "Convert" an enquiry — opens the Create Partner modal pre-filled with
  // the enquiry's name, email, and phone.
  const openCreateFromEnquiry = (enquiry) => {
    setEditingPartner(null);
    setFormData({
      ...defaultFormData,
      name: enquiry.fullName || '',
      email: enquiry.email || '',
      phone: enquiry.phone || '',
    });
    setConvertingEnquiryId(enquiry._id);
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (name === 'commissionValue') {
      setFormData((prev) => ({ ...prev, [name]: parseFloat(value) || 0 }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.name.trim()) {
      setFormError('Partner name is required');
      return;
    }
    if (formData.commissionValue <= 0) {
      setFormError('Commission value must be greater than 0');
      return;
    }
    if (formData.commissionType === 'percentage' && formData.commissionValue > 100) {
      setFormError('Percentage commission cannot exceed 100%');
      return;
    }
    if (formData.payoutMethod === 'bank' && (!formData.payoutAccountHolderName.trim() || !formData.payoutAccountNumber.trim() || !formData.payoutIfsc.trim())) {
      setFormError('Account holder name, account number, and IFSC are required for bank payouts');
      return;
    }
    if (formData.payoutMethod === 'vpa' && !formData.payoutVpa.trim()) {
      setFormError('UPI ID is required for UPI payouts');
      return;
    }

    const {
      payoutMethod, payoutAccountHolderName, payoutAccountNumber, payoutIfsc, payoutVpa,
      password,
      ...rest
    } = formData;

    const payload = {
      ...rest,
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      token: formData.token.trim().toUpperCase(),
      notes: formData.notes.trim(),
      ...(password.trim() ? { password: password.trim() } : {}),
      payout: payoutMethod
        ? {
            method: payoutMethod,
            accountHolderName: payoutAccountHolderName.trim(),
            accountNumber: payoutAccountNumber.trim(),
            ifsc: payoutIfsc.trim().toUpperCase(),
            vpa: payoutVpa.trim(),
          }
        : { method: null },
    };

    if (editingPartner) {
      updateMutation.mutate({ id: editingPartner._id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDelete = (id, name) => {
    if (window.confirm(`Are you sure you want to deactivate "${name}"? This cannot be undone.`)) {
      deleteMutation.mutate(id);
    }
  };

  const openLedger = (partner) => {
    setPayoutError('');
    setLedgerPartner(partner);
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatCommission = (partner) =>
    partner.commissionType === 'percentage' ? `${partner.commissionValue}% commission` : `${inr(partner.commissionValue)} fixed`;

  const formatAutopayFrequency = (frequency) => {
    switch (frequency) {
      case 'daily': return 'Daily';
      case 'weekly': return 'Weekly';
      case 'monthly': return 'Monthly';
      default: return 'Immediate';
    }
  };

  return (
    <div className="partners-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <h1>Channel Partner</h1>
          <p>Create partners, set commissions, and manage payouts</p>
        </div>
        <div className="header-right">
          {activeTab === 'partners' && (
            <>
              <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                <HiOutlinePlus className="w-4 h-4" />
                Create Partner
              </button>
              <button className="btn btn-outline" onClick={openBroadcastModal}>
                <HiOutlineMail className="w-4 h-4" />
                Message Partners
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="partner-tabs">
        <button
          className={`partner-tab${activeTab === 'partners' ? ' active' : ''}`}
          onClick={() => setActiveTab('partners')}
        >
          <HiOutlineUserGroup className="w-4 h-4" />
          Partners
        </button>
        <button
          className={`partner-tab${activeTab === 'enquiries' ? ' active' : ''}`}
          onClick={() => setActiveTab('enquiries')}
        >
          <HiOutlineAnnotation className="w-4 h-4" />
          Enquiries
          {newEnquiryCount > 0 && <span className="partner-tab-badge">{newEnquiryCount}</span>}
        </button>
        <button
          className={`partner-tab${activeTab === 'kyc' ? ' active' : ''}`}
          onClick={() => setActiveTab('kyc')}
        >
          <HiOutlineShieldCheck className="w-4 h-4" />
          KYC Applications
          {pendingKycCount > 0 && <span className="partner-tab-badge">{pendingKycCount}</span>}
        </button>
        <button
          className={`partner-tab${activeTab === 'meetings' ? ' active' : ''}`}
          onClick={() => setActiveTab('meetings')}
        >
          <HiOutlineCalendar className="w-4 h-4" />
          Meeting Requests
          {pendingMeetingCount > 0 && <span className="partner-tab-badge">{pendingMeetingCount}</span>}
        </button>
      </div>

      {activeTab === 'partners' && (
      <>
      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search name, token, email, phone..."
          value={partnerFilters.search}
          onChange={(e) => setPartnerFilter({ search: e.target.value })}
          className="search-input"
        />
        <FilterSelect value={partnerFilters.isActive} onChange={(v) => setPartnerFilter({ isActive: v })}
          allLabel="All Status" options={[['true', 'Active'], ['false', 'Inactive']]} />
        <FilterSelect value={partnerFilters.commissionType} onChange={(v) => setPartnerFilter({ commissionType: v })}
          allLabel="All Commission Types" options={[['percentage', 'Percentage'], ['fixed', 'Fixed']]} />
        <FilterSelect value={partnerFilters.commissionRecurrence} onChange={(v) => setPartnerFilter({ commissionRecurrence: v })}
          allLabel="All Recurrence" options={[['recurring', 'Every payment'], ['first_payment_only', 'First payment only']]} />
        <FilterSelect value={partnerFilters.autoPayout} onChange={(v) => setPartnerFilter({ autoPayout: v })}
          allLabel="Autopay: Any" options={[['true', 'Autopay on'], ['false', 'Autopay off']]} />
        <FilterSelect value={partnerFilters.signupSource} onChange={(v) => setPartnerFilter({ signupSource: v })}
          allLabel="All Signup Sources" options={[['superadmin', 'Created by SuperAdmin'], ['self_signup', 'Self signup']]} />
        <FilterSelect value={partnerFilters.kycStatus} onChange={(v) => setPartnerFilter({ kycStatus: v })}
          allLabel="All KYC Status" options={Object.entries(KYC_STATUS_LABELS)} />
        <DateRangeFilter label="Created" from={partnerFilters.from} to={partnerFilters.to}
          onChange={({ from, to }) => setPartnerFilter({ from, to })} />
        <ResetFiltersButton active={hasActiveFilters(partnerFilters, EMPTY_PARTNER_FILTERS)}
          onReset={() => setPartnerFilter(EMPTY_PARTNER_FILTERS)} />
        <ExportMenu fileName="channel-partners" columns={PARTNER_EXPORT_COLUMNS}
          getRows={() => fetchAllPages(channelPartnerAdminAPI.getAll, partnerApiParams(partnerFilters))} />
      </div>

      {/* Partner List — same table shell/classes as the Admin Users page (.table-container / .data-table / .badge / .action-buttons) */}
      <div className="table-container">
        {isLoading ? (
          <div className="loading">Loading channel partners...</div>
        ) : partners.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <HiOutlineUserGroup className="w-12 h-12 text-gray-300" />
            </div>
            <h3>No channel partners yet</h3>
            <p>Create your first channel partner to start tracking referral commissions</p>
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              <HiOutlinePlus className="w-4 h-4" />
              Create Partner
            </button>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Partner</th>
                  <th>Token</th>
                  <th>Commission</th>
                  <th>Recurrence</th>
                  <th>Earned</th>
                  <th>Paid</th>
                  <th>Pending</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((partner) => {
                  const pending = (partner.totalEarned || 0) - (partner.totalPaid || 0);
                  return (
                    <tr key={partner._id} className={!partner.isActive ? 'row-inactive' : ''}>
                      <td>
                        <div className="partner-cell">
                          <span className="partner-name">{partner.name}</span>
                          {partner.email && <span className="partner-sub">{partner.email}</span>}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                          <span className="partner-token-badge">{partner.token}</span>
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => copyPartnerLink(partner.linkHash)}
                            title="Copy referral link"
                          >
                            <HiOutlineClipboardCopy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      <td>{formatCommission(partner)}</td>
                      <td>
                        <span className="recurrence-badge">
                          {partner.commissionRecurrence === 'recurring' ? 'Every payment' : 'First payment only'}
                        </span>
                        {partner.autoPayoutEnabled && (
                          <span className="recurrence-badge recurrence-badge--autopay" title="Commissions are auto-paid via RazorpayX">
                            <HiOutlineLightningBolt className="w-3 h-3" /> Autopay: {formatAutopayFrequency(partner.autoPayoutFrequency)}
                          </span>
                        )}
                      </td>
                      <td>{inr(partner.totalEarned)}</td>
                      <td>{inr(partner.totalPaid)}</td>
                      <td className={pending > 0 ? 'text-amber' : ''}>{inr(pending)}</td>
                      <td>
                        {partner.isActive ? (
                          <span className="badge badge-success">Active</span>
                        ) : (
                          <span className="badge badge-danger">Inactive</span>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn btn-sm btn-outline" onClick={() => openLedger(partner)} title="Earnings">
                            <HiOutlineCash className="w-3.5 h-3.5" />
                          </button>
                          <button className="btn btn-sm btn-outline" onClick={() => openMessageModal(partner)} title="Message">
                            <HiOutlineMail className="w-3.5 h-3.5" />
                          </button>
                          <button className="btn btn-sm btn-outline" onClick={() => openEditModal(partner)} title="Edit">
                            <HiOutlinePencil className="w-3.5 h-3.5" />
                          </button>
                          {partner.isActive && (
                            <button className="btn btn-sm btn-danger" onClick={() => handleDelete(partner._id, partner.name)} title="Delete">
                              <HiOutlineTrash className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination (server-side: page + limit sent to the API) */}
      {partnerPagination.total > 0 && (
        <div className="pagination">
          <div className="pagination-info">
            <span className="page-info">
              Showing {(partnerPagination.page - 1) * partnerPagination.limit + 1}–
              {Math.min(partnerPagination.page * partnerPagination.limit, partnerPagination.total)}{" "}
              of {partnerPagination.total}
            </span>
            <div className="rows-per-page">
              <label htmlFor="partners-rows-per-page">Rows per page:</label>
              <select
                id="partners-rows-per-page"
                value={partnerFilters.limit}
                onChange={(e) =>
                  setPartnerFilters((prev) => ({ ...prev, limit: Number(e.target.value), page: 1 }))
                }
                className="filter-select filter-select--sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
          {partnerPagination.totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="btn btn-outline btn-sm"
                disabled={partnerFilters.page <= 1}
                onClick={() => setPartnerFilters((prev) => ({ ...prev, page: prev.page - 1 }))}
              >
                Previous
              </button>
              <div className="page-buttons">
                {buildPageList(partnerFilters.page, partnerPagination.totalPages).map((item, idx) =>
                  item === "..." ? (
                    <span key={`ellipsis-${idx}`} className="page-ellipsis">…</span>
                  ) : (
                    <button
                      key={item}
                      className={`btn btn-page${partnerFilters.page === item ? " active" : ""}`}
                      onClick={() => setPartnerFilters((prev) => ({ ...prev, page: item }))}
                    >
                      {item}
                    </button>
                  )
                )}
              </div>
              <button
                className="btn btn-outline btn-sm"
                disabled={partnerFilters.page >= partnerPagination.totalPages}
                onClick={() => setPartnerFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
      </>
      )}

      {activeTab === 'enquiries' && (
      <>
      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search name, email, phone, company, message..."
          value={enquiryFilters.search}
          onChange={(e) => setEnquiryFilter({ search: e.target.value })}
          className="search-input"
        />
        <FilterSelect value={enquiryFilters.type} onChange={(v) => setEnquiryFilter({ type: v })}
          allLabel="All Types" options={[['affiliate', 'Affiliate'], ['query', 'Query']]} />
        <FilterSelect value={enquiryFilters.status} onChange={(v) => setEnquiryFilter({ status: v })}
          allLabel="All Status"
          options={[['new', 'New'], ['contacted', 'Contacted'], ['approved', 'Approved'], ['rejected', 'Rejected'], ['closed', 'Closed']]} />
        <DateRangeFilter label="Submitted" from={enquiryFilters.from} to={enquiryFilters.to}
          onChange={({ from, to }) => setEnquiryFilter({ from, to })} />
        <ResetFiltersButton active={hasActiveFilters(enquiryFilters, EMPTY_ENQUIRY_FILTERS)}
          onReset={() => setEnquiryFilter(EMPTY_ENQUIRY_FILTERS)} />
        <ExportMenu fileName="partner-enquiries" columns={ENQUIRY_EXPORT_COLUMNS}
          getRows={() => fetchAllPages(channelPartnerAdminAPI.getEnquiries, partnerApiParams(enquiryFilters))} />
      </div>

      {/* Enquiries List */}
      <div className="table-container">
        {enquiriesLoading ? (
          <div className="loading">Loading enquiries...</div>
        ) : enquiries.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <HiOutlineMail className="w-12 h-12 text-gray-300" />
            </div>
            <h3>No enquiries yet</h3>
            <p>"Become an Affiliate" and "Any Query" submissions from the landing page's Partner page will show up here</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Company</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {enquiries.map((e) => (
                  <tr key={e._id}>
                    <td>
                      <span className={`badge ${e.type === 'affiliate' ? 'badge-info' : 'badge-secondary'}`}>
                        {e.type === 'affiliate' ? 'Affiliate' : 'Query'}
                      </span>
                    </td>
                    <td>{e.fullName}</td>
                    <td>
                      <div className="partner-cell">
                        <a href={`mailto:${e.email}`} className="partner-sub">{e.email}</a>
                        <a href={`tel:${e.phone}`} className="partner-sub">{e.phone}</a>
                      </div>
                    </td>
                    <td>{e.company || '—'}</td>
                    <td>
                      <select
                        value={e.status}
                        onChange={(ev) => updateEnquiryMutation.mutate({ id: e._id, data: { status: ev.target.value } })}
                        className="filter-select filter-select--sm"
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="closed">Closed</option>
                      </select>
                    </td>
                    <td>{new Date(e.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        {e.message && (
                          <button className="btn btn-sm btn-outline" onClick={() => setEnquiryMessageTarget(e)}>
                            <HiOutlineAnnotation className="w-3.5 h-3.5" />
                            View Message
                          </button>
                        )}
                        <button className="btn btn-sm btn-primary" onClick={() => openCreateFromEnquiry(e)}>
                          <HiOutlineUserAdd className="w-3.5 h-3.5" />
                          Convert
                        </button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDeleteEnquiry(e._id, e.fullName)}>
                          <HiOutlineTrash className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {enquiryPagination.total > 0 && (
        <div className="pagination">
          <div className="pagination-info">
            <span className="page-info">
              Showing {(enquiryPagination.page - 1) * enquiryPagination.limit + 1}–
              {Math.min(enquiryPagination.page * enquiryPagination.limit, enquiryPagination.total)}{" "}
              of {enquiryPagination.total}
            </span>
            <div className="rows-per-page">
              <label htmlFor="enquiries-rows-per-page">Rows per page:</label>
              <select
                id="enquiries-rows-per-page"
                value={enquiryFilters.limit}
                onChange={(e) =>
                  setEnquiryFilters((prev) => ({ ...prev, limit: Number(e.target.value), page: 1 }))
                }
                className="filter-select filter-select--sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
          {enquiryPagination.totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="btn btn-outline btn-sm"
                disabled={enquiryFilters.page <= 1}
                onClick={() => setEnquiryFilters((prev) => ({ ...prev, page: prev.page - 1 }))}
              >
                Previous
              </button>
              <div className="page-buttons">
                {buildPageList(enquiryFilters.page, enquiryPagination.totalPages).map((item, idx) =>
                  item === "..." ? (
                    <span key={`ellipsis-${idx}`} className="page-ellipsis">…</span>
                  ) : (
                    <button
                      key={item}
                      className={`btn btn-page${enquiryFilters.page === item ? " active" : ""}`}
                      onClick={() => setEnquiryFilters((prev) => ({ ...prev, page: item }))}
                    >
                      {item}
                    </button>
                  )
                )}
              </div>
              <button
                className="btn btn-outline btn-sm"
                disabled={enquiryFilters.page >= enquiryPagination.totalPages}
                onClick={() => setEnquiryFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
      </>
      )}

      {activeTab === 'kyc' && (
      <>
      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search name, email, phone..."
          value={kycFilters.search}
          onChange={(e) => setKycFilter({ search: e.target.value })}
          className="search-input"
        />
        <DateRangeFilter label="Submitted" from={kycFilters.from} to={kycFilters.to}
          onChange={({ from, to }) => setKycFilter({ from, to })} />
        <ResetFiltersButton active={hasActiveFilters(kycFilters, EMPTY_KYC_FILTERS)}
          onReset={() => setKycFilter(EMPTY_KYC_FILTERS)} />
        <ExportMenu fileName="kyc-applications" columns={KYC_EXPORT_COLUMNS}
          getRows={() => fetchAllPages(channelPartnerAdminAPI.getAll, { ...partnerApiParams(kycFilters), kycStatus: 'pending' })} />
      </div>

      {/* KYC Applications List — self-signup partners awaiting review */}
      <div className="table-container">
        {kycLoading ? (
          <div className="loading">Loading KYC applications...</div>
        ) : kycPartners.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <HiOutlineShieldCheck className="w-12 h-12 text-gray-300" />
            </div>
            <h3>No pending KYC applications</h3>
            <p>Partners who sign up through the landing page's Partner page will show up here for review</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Country</th>
                  <th>Documents</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {kycPartners.map((p) => (
                  <tr key={p._id}>
                    <td>{p.name}</td>
                    <td>
                      <div className="partner-cell">
                        <a href={`mailto:${p.email}`} className="partner-sub">{p.email}</a>
                        <a href={`tel:${p.phone}`} className="partner-sub">{p.phone}</a>
                      </div>
                    </td>
                    <td>{p.country || '—'}</td>
                    <td>{p.kycDocuments?.length || 0} file{p.kycDocuments?.length === 1 ? '' : 's'}</td>
                    <td>{new Date(p.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn btn-sm btn-primary" onClick={() => openKycReview(p)}>
                          <HiOutlineShieldCheck className="w-3.5 h-3.5" />
                          Review
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {kycPagination.total > 0 && (
        <div className="pagination">
          <div className="pagination-info">
            <span className="page-info">
              Showing {(kycPagination.page - 1) * kycPagination.limit + 1}–
              {Math.min(kycPagination.page * kycPagination.limit, kycPagination.total)}{" "}
              of {kycPagination.total}
            </span>
            <div className="rows-per-page">
              <label htmlFor="kyc-rows-per-page">Rows per page:</label>
              <select
                id="kyc-rows-per-page"
                value={kycFilters.limit}
                onChange={(e) =>
                  setKycFilters((prev) => ({ ...prev, limit: Number(e.target.value), page: 1 }))
                }
                className="filter-select filter-select--sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
          {kycPagination.totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="btn btn-outline btn-sm"
                disabled={kycFilters.page <= 1}
                onClick={() => setKycFilters((prev) => ({ ...prev, page: prev.page - 1 }))}
              >
                Previous
              </button>
              <div className="page-buttons">
                {buildPageList(kycFilters.page, kycPagination.totalPages).map((item, idx) =>
                  item === "..." ? (
                    <span key={`ellipsis-${idx}`} className="page-ellipsis">…</span>
                  ) : (
                    <button
                      key={item}
                      className={`btn btn-page${kycFilters.page === item ? " active" : ""}`}
                      onClick={() => setKycFilters((prev) => ({ ...prev, page: item }))}
                    >
                      {item}
                    </button>
                  )
                )}
              </div>
              <button
                className="btn btn-outline btn-sm"
                disabled={kycFilters.page >= kycPagination.totalPages}
                onClick={() => setKycFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
      </>
      )}

      {activeTab === 'meetings' && (
      <>
      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search lead name, phone, email, company..."
          value={meetingFilters.search}
          onChange={(e) => setMeetingFilter({ search: e.target.value })}
          className="search-input"
        />
        <FilterSelect value={meetingFilters.status} onChange={(v) => setMeetingFilter({ status: v })}
          allLabel="All Status"
          options={[['pending', 'Pending'], ['scheduled', 'Scheduled'], ['completed', 'Completed'], ['cancelled', 'Cancelled']]} />
        {meetingPartnerOptions.length > 0 && (
          <FilterSelect value={meetingFilters.channelPartnerId} onChange={(v) => setMeetingFilter({ channelPartnerId: v })}
            allLabel="All Partners" className="max-w-[220px]"
            options={meetingPartnerOptions.map((p) => [p._id, p.name])} />
        )}
        <DateRangeFilter label="Requested" from={meetingFilters.from} to={meetingFilters.to}
          onChange={({ from, to }) => setMeetingFilter({ from, to })} />
        <DateRangeFilter label="Preferred" from={meetingFilters.preferredFrom} to={meetingFilters.preferredTo}
          onChange={({ from, to }) => setMeetingFilter({ preferredFrom: from, preferredTo: to })} />
        <ResetFiltersButton active={hasActiveFilters(meetingFilters, EMPTY_MEETING_FILTERS)}
          onReset={() => setMeetingFilter(EMPTY_MEETING_FILTERS)} />
        <ExportMenu fileName="partner-meeting-requests" columns={MEETING_EXPORT_COLUMNS}
          getRows={() => fetchAllPages(channelPartnerAdminAPI.getPartnerMeetingRequests, partnerApiParams(meetingFilters))} />
      </div>

      {/* Partner Meeting Requests */}
      <div className="table-container">
        {meetingsLoading ? (
          <div className="loading">Loading meeting requests...</div>
        ) : meetingRequests.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <HiOutlineCalendar className="w-12 h-12 text-gray-300" />
            </div>
            <h3>No meeting requests yet</h3>
            <p>When a partner asks your team to meet one of their leads directly, it'll show up here</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Partner</th>
                  <th>Lead</th>
                  <th>Contact</th>
                  <th>Preferred Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {meetingRequests.map((r) => (
                  <tr key={r._id}>
                    <td>
                      <div className="partner-cell">
                        <span>{r.channelPartnerId?.name || '—'}</span>
                        <span className="partner-sub">{r.channelPartnerId?.email}</span>
                      </div>
                    </td>
                    <td>
                      <div className="partner-cell">
                        <span>{r.leadName}</span>
                        {r.company && <span className="partner-sub">{r.company}</span>}
                      </div>
                    </td>
                    <td>
                      <div className="partner-cell">
                        <a href={`tel:${r.leadPhone}`} className="partner-sub">{r.leadPhone}</a>
                        {r.leadEmail && <a href={`mailto:${r.leadEmail}`} className="partner-sub">{r.leadEmail}</a>}
                      </div>
                    </td>
                    <td>{formatDate(r.preferredDate)}</td>
                    <td>
                      <span className={`badge ${
                        r.status === 'completed' ? 'badge-success' :
                        r.status === 'scheduled' ? 'badge-info' :
                        r.status === 'cancelled' ? 'badge-danger' : 'badge-warning'
                      }`}>
                        {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn btn-sm btn-outline" onClick={() => openMeetingEdit(r)}>
                          <HiOutlinePencil className="w-3.5 h-3.5" />
                          Update
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {meetingPagination.totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-controls">
            <button
              className="btn btn-outline btn-sm"
              disabled={meetingFilters.page <= 1}
              onClick={() => setMeetingFilters((prev) => ({ ...prev, page: prev.page - 1 }))}
            >
              Previous
            </button>
            <span className="page-info">Page {meetingFilters.page} of {meetingPagination.totalPages}</span>
            <button
              className="btn btn-outline btn-sm"
              disabled={meetingFilters.page >= meetingPagination.totalPages}
              onClick={() => setMeetingFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
            >
              Next
            </button>
          </div>
        </div>
      )}
      </>
      )}

      {/* ── Create / Edit Modal ── */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="partner-modal" onClick={(e) => e.stopPropagation()}>
            <div className="partner-modal__header">
              <div>
                <h2>{editingPartner ? 'Edit Channel Partner' : 'Create Channel Partner'}</h2>
                <p className="partner-modal__subtitle">
                  {editingPartner ? `Modify ${editingPartner.name}` : 'Configure a new referral partner and their commission'}
                </p>
              </div>
              <button className="partner-modal__close" onClick={closeModal}>
                <HiOutlineX className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="partner-modal__body">
              {formError && (
                <div className="partner-modal__error">
                  <HiOutlineX className="w-4 h-4" />
                  {formError}
                </div>
              )}

              <div className="partner-modal__grid partner-modal__grid--2col">
                <div className="partner-modal__field">
                  <label>Partner Name <span className="required">*</span></label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="e.g. Acme Referrals" required />
                </div>
                <div className="partner-modal__field">
                  <label>Partner Token</label>
                  <input
                    type="text"
                    name="token"
                    value={formData.token}
                    onChange={handleInputChange}
                    placeholder="Auto-generated if left blank"
                    disabled={!!editingPartner}
                    className={editingPartner ? 'field-disabled' : ''}
                    style={{ textTransform: 'uppercase' }}
                  />
                  {editingPartner && <span className="field-hint">Token cannot be changed after creation</span>}
                </div>
              </div>

              <div className="partner-modal__grid partner-modal__grid--2col">
                <div className="partner-modal__field">
                  <label>Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="partner@example.com" />
                </div>
                <div className="partner-modal__field">
                  <label>Phone</label>
                  <input type="text" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+91 9876543210" />
                </div>
              </div>

              <div className="partner-modal__field">
                <label>Portal Password</label>
                <div className="partner-password-field">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder={editingPartner ? 'Leave blank to keep unchanged' : 'Set a password for partner login'}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="partner-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <HiOutlineEyeOff className="w-4 h-4" /> : <HiOutlineEye className="w-4 h-4" />}
                  </button>
                </div>
                <span className="field-hint">
                  {editingPartner
                    ? 'Only fill this in to reset the password. If an email is on file, new credentials will be sent.'
                    : 'If an email is on file, login credentials will be emailed to the partner.'}
                </span>
              </div>

              <div className="partner-modal__section-title">Commission</div>

              <div className="partner-modal__grid partner-modal__grid--3col">
                <div className="partner-modal__field">
                  <label>Commission Type <span className="required">*</span></label>
                  <select name="commissionType" value={formData.commissionType} onChange={handleInputChange}>
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>
                <div className="partner-modal__field">
                  <label>Commission Value <span className="required">*</span></label>
                  <div className="input-with-suffix">
                    <input
                      type="number"
                      name="commissionValue"
                      value={formData.commissionValue}
                      onChange={handleInputChange}
                      min="0"
                      step={formData.commissionType === 'percentage' ? '1' : '0.01'}
                      required
                    />
                    <span className="input-suffix">{formData.commissionType === 'percentage' ? '%' : '₹'}</span>
                  </div>
                </div>
                <div className="partner-modal__field">
                  <label>Commission Recurrence</label>
                  <select name="commissionRecurrence" value={formData.commissionRecurrence} onChange={handleInputChange}>
                    <option value="first_payment_only">First payment only</option>
                    <option value="recurring">Every successful payment</option>
                  </select>
                </div>
              </div>

              <div className="partner-modal__section-title">
                Payout Details <span className="field-hint" style={{ display: 'inline', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>— only needed for the automated "Pay via Razorpay" button; manual "Mark Paid" works without this</span>
              </div>

              <div className="partner-modal__field">
                <label>Payout Method</label>
                <select name="payoutMethod" value={formData.payoutMethod} onChange={handleInputChange}>
                  <option value="">Not set (manual payout only)</option>
                  <option value="bank">Bank Account</option>
                  <option value="vpa">UPI</option>
                </select>
              </div>

              {formData.payoutMethod === 'bank' && (
                <div className="partner-modal__grid partner-modal__grid--2col">
                  <div className="partner-modal__field">
                    <label>Account Holder Name</label>
                    <input type="text" name="payoutAccountHolderName" value={formData.payoutAccountHolderName} onChange={handleInputChange} placeholder="As per bank records" />
                  </div>
                  <div className="partner-modal__field">
                    <label>IFSC Code</label>
                    <input type="text" name="payoutIfsc" value={formData.payoutIfsc} onChange={handleInputChange} placeholder="e.g. HDFC0001234" style={{ textTransform: 'uppercase' }} />
                  </div>
                  <div className="partner-modal__field" style={{ gridColumn: '1 / -1' }}>
                    <label>Account Number</label>
                    <input type="text" name="payoutAccountNumber" value={formData.payoutAccountNumber} onChange={handleInputChange} placeholder="Bank account number" />
                  </div>
                </div>
              )}

              {formData.payoutMethod === 'vpa' && (
                <div className="partner-modal__field">
                  <label>UPI ID</label>
                  <input type="text" name="payoutVpa" value={formData.payoutVpa} onChange={handleInputChange} placeholder="partner@upi" />
                </div>
              )}

              <div className="partner-modal__field">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    name="autoPayoutEnabled"
                    checked={formData.autoPayoutEnabled}
                    onChange={handleInputChange}
                  />
                  <HiOutlineLightningBolt className="w-4 h-4" /> Auto-payout via RazorpayX
                </label>
                <span className="field-hint">
                  Commissions are paid out automatically instead of waiting for manual "Mark Paid" /
                  "Pay via Razorpay". Needs a payout method above and RazorpayX configured.
                </span>
              </div>

              {formData.autoPayoutEnabled && (
                <div className="partner-modal__field">
                  <label>Autopay Frequency</label>
                  <select name="autoPayoutFrequency" value={formData.autoPayoutFrequency} onChange={handleInputChange}>
                    <option value="immediate">Immediately (as soon as earned)</option>
                    <option value="daily">Daily (10:30 AM)</option>
                    <option value="weekly">Weekly (Monday 10:30 AM)</option>
                    <option value="monthly">Monthly (1st, 10:30 AM)</option>
                  </select>
                  <span className="field-hint">How often THIS partner's pending commissions are paid out.</span>
                </div>
              )}

              <div className="partner-modal__field">
                <label>Notes</label>
                <textarea name="notes" value={formData.notes} onChange={handleInputChange} placeholder="Internal notes about this partner" rows={2} />
              </div>

              <div className="partner-modal__field">
                <label className="toggle-label">
                  <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleInputChange} />
                  {formData.isActive ? (
                    <><HiOutlineEye className="w-4 h-4" /> Active</>
                  ) : (
                    <><HiOutlineEyeOff className="w-4 h-4" /> Inactive</>
                  )}
                </label>
              </div>

              <div className="partner-modal__actions">
                <button type="button" className="btn btn-outline" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending || updateMutation.isPending ? 'Saving...' : editingPartner ? 'Update Partner' : 'Create Partner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Earnings / Ledger Modal ── */}
      {ledgerPartner && (
        <div className="modal-overlay" onClick={() => setLedgerPartner(null)}>
          <div className="ledger-modal" onClick={(e) => e.stopPropagation()}>
            <div className="partner-modal__header">
              <div>
                <h2>{ledgerPartner.name} — Earnings</h2>
                <p className="partner-modal__subtitle">Token {ledgerPartner.token}</p>
              </div>
              <button className="partner-modal__close" onClick={() => setLedgerPartner(null)}>
                <HiOutlineX className="w-5 h-5" />
              </button>
            </div>

            <div className="ledger-modal__body">
              <div className="ledger-totals">
                <div className="ledger-total-card">
                  <span className="ledger-total-label">Pending</span>
                  <span className="ledger-total-value text-amber">{inr(totals.pending)}</span>
                </div>
                <div className="ledger-total-card">
                  <span className="ledger-total-label">Paid</span>
                  <span className="ledger-total-value text-green">{inr(totals.paid)}</span>
                </div>
              </div>

              {payoutError && (
                <div className="partner-modal__error">
                  <HiOutlineX className="w-4 h-4" />
                  {payoutError}
                </div>
              )}

              {ledgerLoading ? (
                <div className="loading-state">
                  <div className="loading-spinner" />
                  <p>Loading earnings...</p>
                </div>
              ) : commissions.length === 0 ? (
                <div className="empty-state">
                  <HiOutlineCurrencyRupee className="w-10 h-10 text-gray-300" />
                  <p>No commissions recorded yet for this partner</p>
                </div>
              ) : (
                <div className="partners-table-wrapper">
                  <table className="partners-table">
                    <thead>
                      <tr>
                        <th>Organization</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Paid</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commissions.map((c) => (
                        <tr key={c._id}>
                          <td>{c.organizationId?.name || c.organizationName || '—'}</td>
                          <td>{inr(c.amount)}</td>
                          <td>
                            <span className={`status-badge status-${c.status === 'paid' ? 'active' : c.status === 'failed' ? 'expired' : 'scheduled'}`}>
                              {c.status === 'processing' ? 'Processing' : c.status}
                            </span>
                            {c.paidVia && <span className="partner-sub" style={{ marginLeft: 6 }}>via {c.paidVia}</span>}
                          </td>
                          <td>{c.status === 'paid' && c.amountPaid != null ? inr(c.amountPaid) : '—'}</td>
                          <td>{formatDate(c.createdAt)}</td>
                          <td>
                            <div className="action-btns">
                              {c.status === 'pending' && (
                                <>
                                  <button className="btn btn-outline btn-sm" onClick={() => openMarkPaidModal(c)}>
                                    Mark Paid
                                  </button>
                                  <button
                                    className="btn btn-primary btn-sm"
                                    disabled={payoutMutation.isPending}
                                    onClick={() => payoutMutation.mutate(c._id)}
                                  >
                                    Pay via Razorpay
                                  </button>
                                </>
                              )}
                              {c.status === 'paid' && remainingOwed(c) > 0 && (
                                <button className="btn btn-outline btn-sm" onClick={() => openMarkPaidModal(c)}>
                                  Pay Remaining ({inr(remainingOwed(c))})
                                </button>
                              )}
                              {(c.payoutReference || c.failureReason) && (
                                <button className="btn btn-outline btn-sm" onClick={() => setNoteModalTarget(c)}>
                                  <HiOutlineDocumentText className="w-3.5 h-3.5" />
                                  View Note
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Mark Paid confirmation modal (replaces window.prompt) ── */}
      {markPaidTarget && (
        <div className="modal-overlay" onClick={closeMarkPaidModal}>
          <div className="partner-modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="partner-modal__header">
              <div>
                <h2>{markPaidTarget.status === 'paid' ? 'Pay Remaining Balance' : 'Mark Commission as Paid'}</h2>
                <p className="partner-modal__subtitle">
                  {markPaidTarget.organizationId?.name || markPaidTarget.organizationName || 'This organization'} · {markPaidTarget.status === 'paid' ? 'remaining' : 'owed'} {inr(remainingOwed(markPaidTarget))}
                </p>
              </div>
              <button className="partner-modal__close" onClick={closeMarkPaidModal}>
                <HiOutlineX className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={submitMarkPaid} className="partner-modal__body">
              {markPaidError && (
                <div className="partner-modal__error">
                  <HiOutlineX className="w-4 h-4" />
                  {markPaidError}
                </div>
              )}

              <div className="partner-modal__field">
                <label>Amount to Pay</label>
                <input type="text" value={inr(remainingOwed(markPaidTarget))} disabled className="field-disabled" />
                <span className="field-hint">The full remaining balance — partial payments aren't allowed.</span>
              </div>

              <div className="partner-modal__field">
                <label>Payment Reference / Note</label>
                <input
                  type="text"
                  value={markPaidReference}
                  onChange={(e) => setMarkPaidReference(e.target.value)}
                  placeholder="e.g. UTR number, UPI ref, or a short note"
                />
              </div>

              <div className="partner-modal__actions">
                <button type="button" className="btn btn-outline" onClick={closeMarkPaidModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={markPaidMutation.isPending}>
                  {markPaidMutation.isPending ? 'Saving...' : 'Confirm Paid'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── View Note modal — full payoutReference / failureReason text ── */}
      {noteModalTarget && (
        <div className="modal-overlay" onClick={() => setNoteModalTarget(null)}>
          <div className="partner-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="partner-modal__header">
              <div>
                <h2>Commission Note</h2>
                <p className="partner-modal__subtitle">
                  {noteModalTarget.organizationId?.name || noteModalTarget.organizationName || 'This organization'} · {inr(noteModalTarget.amount)} · {formatDate(noteModalTarget.createdAt)}
                </p>
              </div>
              <button className="partner-modal__close" onClick={() => setNoteModalTarget(null)}>
                <HiOutlineX className="w-5 h-5" />
              </button>
            </div>
            <div className="partner-modal__body">
              {noteModalTarget.status === 'paid' && (
                <div className="partner-modal__field">
                  <label>Amount Paid</label>
                  <p className="note-text">
                    {noteModalTarget.amountPaid != null ? inr(noteModalTarget.amountPaid) : '—'}
                    {noteModalTarget.paidVia && ` (via ${noteModalTarget.paidVia})`}
                  </p>
                </div>
              )}
              {noteModalTarget.payoutReference && (
                <div className="partner-modal__field">
                  <label>Payment Reference / Note</label>
                  <p className="note-text">{noteModalTarget.payoutReference}</p>
                </div>
              )}
              {noteModalTarget.failureReason && (
                <div className="partner-modal__field">
                  <label>Failure Reason</label>
                  <p className="note-text note-text--error">{noteModalTarget.failureReason}</p>
                </div>
              )}
              <div className="partner-modal__actions">
                <button type="button" className="btn btn-outline" onClick={() => setNoteModalTarget(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── View Message modal — an enquiry's full message text ── */}
      {enquiryMessageTarget && (
        <div className="modal-overlay" onClick={() => setEnquiryMessageTarget(null)}>
          <div className="partner-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="partner-modal__header">
              <div>
                <h2>{enquiryMessageTarget.fullName}'s Message</h2>
                <p className="partner-modal__subtitle">
                  {enquiryMessageTarget.type === 'affiliate' ? 'Affiliate Application' : 'Query'} · {formatDate(enquiryMessageTarget.createdAt)}
                </p>
              </div>
              <button className="partner-modal__close" onClick={() => setEnquiryMessageTarget(null)}>
                <HiOutlineX className="w-5 h-5" />
              </button>
            </div>
            <div className="partner-modal__body">
              <div className="partner-modal__field">
                <p className="note-text">{enquiryMessageTarget.message}</p>
              </div>
              <div className="partner-modal__actions">
                <button type="button" className="btn btn-outline" onClick={() => setEnquiryMessageTarget(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── KYC Review modal — applicant info + documents + approve (with commission/payout) or reject ── */}
      {kycReviewTarget && kycReviewForm && (
        <div className="modal-overlay" onClick={closeKycReview}>
          <div className="partner-modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            <div className="partner-modal__header">
              <div>
                <h2>Review KYC Application</h2>
                <p className="partner-modal__subtitle">
                  {kycReviewTarget.name} · {kycReviewTarget.email} · {kycReviewTarget.country || '—'}
                </p>
              </div>
              <button className="partner-modal__close" onClick={closeKycReview}>
                <HiOutlineX className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={submitKycApprove} className="partner-modal__body">
              {kycReviewError && (
                <div className="partner-modal__error">
                  <HiOutlineX className="w-4 h-4" />
                  {kycReviewError}
                </div>
              )}

              <div className="partner-modal__section-title">Submitted Documents</div>
              {kycReviewTarget.kycDocuments?.length > 0 ? (
                <ul className="kyc-doc-list">
                  {kycReviewTarget.kycDocuments.map((doc) => (
                    <li key={doc.key}>
                      <a href={filePublicUrl(doc.fileUrl)} target="_blank" rel="noopener noreferrer">
                        <HiOutlineDocumentDownload className="w-4 h-4" />
                        {doc.label || doc.key}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="field-hint">No documents submitted.</p>
              )}

              <div className="partner-modal__section-title">Commission (applies on approval)</div>
              <div className="partner-modal__grid partner-modal__grid--3col">
                <div className="partner-modal__field">
                  <label>Commission Type <span className="required">*</span></label>
                  <select
                    value={kycReviewForm.commissionType}
                    onChange={(e) => setKycReviewForm((prev) => ({ ...prev, commissionType: e.target.value }))}
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>
                <div className="partner-modal__field">
                  <label>Commission Value <span className="required">*</span></label>
                  <div className="input-with-suffix">
                    <input
                      type="number"
                      value={kycReviewForm.commissionValue}
                      onChange={(e) => setKycReviewForm((prev) => ({ ...prev, commissionValue: e.target.value }))}
                      min="0"
                      step={kycReviewForm.commissionType === 'percentage' ? '1' : '0.01'}
                      required
                    />
                    <span className="input-suffix">{kycReviewForm.commissionType === 'percentage' ? '%' : '₹'}</span>
                  </div>
                </div>
                <div className="partner-modal__field">
                  <label>Recurrence</label>
                  <select
                    value={kycReviewForm.commissionRecurrence}
                    onChange={(e) => setKycReviewForm((prev) => ({ ...prev, commissionRecurrence: e.target.value }))}
                  >
                    <option value="first_payment_only">First payment only</option>
                    <option value="recurring">Every successful payment</option>
                  </select>
                </div>
              </div>

              <p className="field-hint">
                Payout (bank/UPI) details are set by the partner themselves from their own Settings page
                once approved — not here, since only they know their own account details.
              </p>

              <div className="partner-modal__section-title">Reject Instead?</div>
              <div className="partner-modal__field">
                <label>Rejection Reason (sent to the applicant by email)</label>
                <textarea
                  rows={2}
                  value={kycRejectionReason}
                  onChange={(e) => setKycRejectionReason(e.target.value)}
                  placeholder="e.g. PAN card image is unreadable, please resubmit"
                />
              </div>

              <div className="partner-modal__actions">
                <button type="button" className="btn btn-danger" disabled={reviewKycMutation.isPending} onClick={submitKycReject}>
                  Reject
                </button>
                <button type="submit" className="btn btn-primary" disabled={reviewKycMutation.isPending}>
                  <HiOutlineCheck className="w-4 h-4" />
                  {reviewKycMutation.isPending ? 'Saving...' : 'Approve & Activate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Message modal — compose box + history, appears on the partner's dashboard Notice Board ── */}
      {messageTarget && (
        <div className="modal-overlay" onClick={closeMessageModal}>
          <div className="partner-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="partner-modal__header">
              <div>
                <h2>Message {messageTarget.name}</h2>
                <p className="partner-modal__subtitle">Appears on their dashboard's Notice Board</p>
              </div>
              <button className="partner-modal__close" onClick={closeMessageModal}>
                <HiOutlineX className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={submitMessage} className="partner-modal__body">
              <div className="partner-modal__field">
                <label>New Message</label>
                <textarea
                  rows={3}
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="e.g. Your commission rate has been updated to 12%"
                  maxLength={1000}
                  required
                />
              </div>
              <div className="partner-modal__field">
                <label>Expires On <span className="field-hint" style={{ display: 'inline', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>— optional, stops showing on their Notice Board after this date</span></label>
                <input
                  type="date"
                  value={messageExpiresAt}
                  onChange={(e) => setMessageExpiresAt(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="partner-modal__actions" style={{ justifyContent: 'flex-start' }}>
                <button type="submit" className="btn btn-primary" disabled={sendMessageMutation.isPending || !messageText.trim()}>
                  <HiOutlineMail className="w-4 h-4" />
                  {sendMessageMutation.isPending ? 'Sending...' : 'Send'}
                </button>
              </div>

              <div className="partner-modal__section-title" style={{ marginTop: '1.5rem' }}>Previously Sent</div>
              {messageHistoryLoading ? (
                <p className="field-hint">Loading...</p>
              ) : messageHistory.length === 0 ? (
                <p className="field-hint">No messages sent to this partner yet.</p>
              ) : (
                <ul className="message-history-list">
                  {messageHistory.map((m) => {
                    const expired = m.expiresAt && new Date(m.expiresAt) < new Date();
                    return (
                      <li key={m._id}>
                        <p className="note-text">{m.message}</p>
                        <span className="field-hint">
                          {formatDate(m.createdAt)}
                          {m.expiresAt && (expired ? ' · Expired' : ` · Expires ${formatDate(m.expiresAt)}`)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </form>
          </div>
        </div>
      )}

      {/* ── Meeting Request Update Modal ── */}
      {meetingEditTarget && (
        <div className="modal-overlay" onClick={() => setMeetingEditTarget(null)}>
          <div className="partner-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="partner-modal__header">
              <div>
                <h2>{meetingEditTarget.leadName}</h2>
                <p className="partner-modal__subtitle">
                  Requested by {meetingEditTarget.channelPartnerId?.name || 'a partner'}
                </p>
              </div>
              <button className="partner-modal__close" onClick={() => setMeetingEditTarget(null)}>
                <HiOutlineX className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={submitMeetingEdit} className="partner-modal__body">
              <div className="partner-modal__field">
                <label>Lead Contact</label>
                <p className="note-text">
                  <HiOutlinePhone className="w-3.5 h-3.5" style={{ display: 'inline', marginRight: 4 }} />
                  {meetingEditTarget.leadPhone}
                  {meetingEditTarget.leadEmail ? ` · ${meetingEditTarget.leadEmail}` : ''}
                </p>
              </div>
              {meetingEditTarget.notes && (
                <div className="partner-modal__field">
                  <label>Partner's Notes</label>
                  <p className="note-text">{meetingEditTarget.notes}</p>
                </div>
              )}
              <div className="partner-modal__field">
                <label>Status</label>
                <select
                  value={meetingEditForm.status}
                  onChange={(e) => setMeetingEditForm((prev) => ({ ...prev, status: e.target.value }))}
                  className="filter-select"
                >
                  <option value="pending">Pending</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              {meetingEditForm.status === 'scheduled' && (
                <div className="partner-modal__field">
                  <label>Scheduled Date</label>
                  <input
                    type="date"
                    value={meetingEditForm.scheduledAt}
                    onChange={(e) => setMeetingEditForm((prev) => ({ ...prev, scheduledAt: e.target.value }))}
                  />
                </div>
              )}
              <div className="partner-modal__field">
                <label>Notes to Partner <span className="field-hint" style={{ display: 'inline', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>— optional, shown on their request</span></label>
                <textarea
                  rows={2}
                  value={meetingEditForm.superadminNotes}
                  onChange={(e) => setMeetingEditForm((prev) => ({ ...prev, superadminNotes: e.target.value }))}
                  placeholder="e.g. Scheduled for 3pm with our sales lead"
                />
              </div>
              <div className="partner-modal__actions">
                <button type="button" className="btn btn-outline" onClick={() => setMeetingEditTarget(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={updateMeetingMutation.isPending}>
                  {updateMeetingMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Broadcast modal — message all partners, or a selected subset ── */}
      {showBroadcastModal && (
        <div className="modal-overlay" onClick={closeBroadcastModal}>
          <div className="partner-modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div className="partner-modal__header">
              <div>
                <h2>Message Partners</h2>
                <p className="partner-modal__subtitle">Appears on each partner's dashboard Notice Board</p>
              </div>
              <button className="partner-modal__close" onClick={closeBroadcastModal}>
                <HiOutlineX className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={submitBroadcast} className="partner-modal__body">
              {broadcastError && (
                <div className="partner-modal__error">
                  <HiOutlineX className="w-4 h-4" />
                  {broadcastError}
                </div>
              )}

              <div className="partner-modal__field">
                <label>Send To</label>
                <div className="broadcast-mode-toggle">
                  <button
                    type="button"
                    className={`broadcast-mode-btn${broadcastMode === 'all' ? ' active' : ''}`}
                    onClick={() => setBroadcastMode('all')}
                  >
                    All Active Partners
                  </button>
                  <button
                    type="button"
                    className={`broadcast-mode-btn${broadcastMode === 'select' ? ' active' : ''}`}
                    onClick={() => setBroadcastMode('select')}
                  >
                    Select Partners
                  </button>
                </div>
              </div>

              {broadcastMode === 'select' && (
                <div className="partner-modal__field">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.375rem' }}>
                    <label style={{ margin: 0 }}>
                      Partners{broadcastSelectedIds.length > 0 ? ` (${broadcastSelectedIds.length} selected)` : ''}
                    </label>
                    {broadcastablePartners.length > 0 && (
                      <button
                        type="button"
                        className="broadcast-select-all-link"
                        onClick={() =>
                          setBroadcastSelectedIds(
                            broadcastSelectedIds.length === broadcastablePartners.length
                              ? []
                              : broadcastablePartners.map((p) => p._id)
                          )
                        }
                      >
                        {broadcastSelectedIds.length === broadcastablePartners.length ? 'Clear all' : 'Select all'}
                      </button>
                    )}
                  </div>
                  <div className="broadcast-partner-list">
                    {broadcastPartnersLoading ? (
                      <p className="field-hint" style={{ padding: '0.75rem' }}>Loading partners...</p>
                    ) : broadcastablePartners.length === 0 ? (
                      <p className="field-hint" style={{ padding: '0.75rem' }}>No active partners found.</p>
                    ) : (
                      broadcastablePartners.map((p) => (
                        <label key={p._id} className="broadcast-partner-row">
                          <input
                            type="checkbox"
                            checked={broadcastSelectedIds.includes(p._id)}
                            onChange={() => toggleBroadcastSelected(p._id)}
                          />
                          <span className="partner-cell">
                            <span className="partner-name">{p.name}</span>
                            {p.email && <span className="partner-sub">{p.email}</span>}
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}

              <div className="partner-modal__field">
                <label>Message</label>
                <textarea
                  rows={3}
                  value={broadcastText}
                  onChange={(e) => setBroadcastText(e.target.value)}
                  placeholder="e.g. Payouts will be processed a day early this month due to a holiday"
                  maxLength={1000}
                  required
                />
              </div>

              <div className="partner-modal__field">
                <label>Expires On <span className="field-hint" style={{ display: 'inline', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>— optional, stops showing after this date</span></label>
                <input
                  type="date"
                  value={broadcastExpiresAt}
                  onChange={(e) => setBroadcastExpiresAt(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="partner-modal__actions">
                <button type="button" className="btn btn-outline" onClick={closeBroadcastModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={broadcastMessageMutation.isPending}>
                  <HiOutlineMail className="w-4 h-4" />
                  {broadcastMessageMutation.isPending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Scoped Styles ── */}
      <style>{`
        .partners-page { max-width: 100%; }
        .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; }
        .header-left h1 { font-size: 1.5rem; font-weight: 700; color: #111827; margin: 0; }
        .header-left p { font-size: 0.875rem; color: #6b7280; margin: 0.25rem 0 0; }
        .partner-tabs { display: flex; gap: 0.5rem; margin-bottom: 1.25rem; border-bottom: 1px solid #e5e7eb; }
        .partner-tab { display: inline-flex; align-items: center; gap: 0.375rem; padding: 0.625rem 0.25rem; margin-bottom: -1px; font-size: 0.875rem; font-weight: 600; color: #6b7280; background: none; border: none; border-bottom: 2px solid transparent; cursor: pointer; transition: all 0.15s; }
        .partner-tab + .partner-tab { margin-left: 1rem; }
        .partner-tab:hover { color: #374151; }
        .partner-tab.active { color: #6366f1; border-bottom-color: #6366f1; }
        .partner-tab-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 1.25rem; height: 1.25rem; padding: 0 0.3rem; border-radius: 9999px; background: #ef4444; color: #fff; font-size: 0.6875rem; font-weight: 700; }
        html.dark .partner-tabs { border-bottom-color: #2a3158; }
        html.dark .partner-tab { color: #8ea1b8; }
        html.dark .partner-tab:hover { color: #cbd5e1; }
        html.dark .partner-tab.active { color: #818cf8; border-bottom-color: #818cf8; }
        .btn { display: inline-flex; align-items: center; justify-content: center; gap: 0.375rem; padding: 0.5rem 1rem; font-size: 0.875rem; font-weight: 500; border-radius: 0.5rem; border: none; cursor: pointer; transition: all 0.15s; text-decoration: none; }
        .btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .btn-primary { background: #6366f1; color: #fff; }
        .btn-primary:hover:not(:disabled) { background: #4f46e5; }
        .btn-outline { background: transparent; border: 1.5px solid #d1d5db; color: #374151; }
        .btn-outline:hover:not(:disabled) { background: #f9fafb; border-color: #9ca3af; }
        .btn-danger { background: #ef4444; color: #fff; }
        .btn-danger:hover:not(:disabled) { background: #dc2626; }
        .btn-sm { padding: 0.375rem 0.75rem; font-size: 0.75rem; }

        .loading-state { display: flex; flex-direction: column; align-items: center; gap: 1rem; padding: 3rem; color: #6b7280; }
        .loading-spinner { width: 2rem; height: 2rem; border: 3px solid #e5e7eb; border-top-color: #6366f1; border-radius: 50%; animation: spin 0.8s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }

        .empty-state { display: flex; flex-direction: column; align-items: center; gap: 0.75rem; padding: 4rem 2rem; text-align: center; }
        .empty-state h3 { font-size: 1.125rem; font-weight: 600; color: #374151; }
        .empty-state p { font-size: 0.875rem; color: #6b7280; }

        .partners-table-wrapper { overflow-x: auto; }
        .partners-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .partners-table th { background: #f9fafb; padding: 0.75rem 1rem; text-align: left; font-size: 0.75rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e5e7eb; }
        .partners-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #f3f4f6; color: #374151; }
        .partners-table tr:hover { background: #f9fafb; }
        tr.row-inactive { opacity: 0.55; }

        .partner-cell { display: flex; flex-direction: column; gap: 0.125rem; }
        .partner-name { font-weight: 600; color: #111827; }
        .partner-sub { font-size: 0.75rem; color: #9ca3af; }
        .partner-token-badge { display: inline-block; font-family: monospace; font-size: 0.875rem; font-weight: 700; background: #eef2ff; color: #4f46e5; padding: 0.125rem 0.5rem; border-radius: 0.25rem; letter-spacing: 0.05em; }
        .recurrence-badge { display: inline-flex; align-items: center; gap: 0.25rem; font-size: 0.6875rem; font-weight: 600; background: #f3f4f6; color: #4b5563; padding: 0.125rem 0.5rem; border-radius: 9999px; margin-top: 0.25rem; }
        .recurrence-badge:first-child { margin-top: 0; }
        .recurrence-badge--autopay { background: #fef3c7; color: #92400e; display: flex; width: fit-content; }
        .text-amber { color: #b45309; font-weight: 600; }
        .text-green { color: #059669; font-weight: 600; }

        .status-badge { display: inline-block; padding: 0.125rem 0.5rem; border-radius: 9999px; font-size: 0.6875rem; font-weight: 600; }
        .status-active { background: #dcfce7; color: #166534; }
        .status-inactive { background: #f3f4f6; color: #6b7280; }
        .status-expired { background: #fef2f2; color: #991b1b; }
        .status-scheduled { background: #fef3c7; color: #92400e; }

        .action-btns { display: flex; gap: 0.375rem; flex-wrap: wrap; }

        .note-text { margin: 0; font-size: 0.875rem; color: #111827; white-space: pre-wrap; }
        .note-text--error { color: #991b1b; }

        .kyc-doc-list { list-style: none; margin: 0 0 1rem; padding: 0; display: flex; flex-direction: column; gap: 0.5rem; }
        .kyc-doc-list a { display: inline-flex; align-items: center; gap: 0.5rem; font-size: 0.875rem; color: #6366f1; text-decoration: none; padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb; border-radius: 0.5rem; transition: background 0.15s; }
        .kyc-doc-list a:hover { background: #f9fafb; }
        html.dark .kyc-doc-list a { color: #818cf8; border-color: #2a3158; }
        html.dark .kyc-doc-list a:hover { background: #1a2140; }

        .message-history-list { list-style: none; margin: 0.75rem 0 0; padding: 0; display: flex; flex-direction: column; gap: 0.75rem; max-height: 220px; overflow-y: auto; }
        .message-history-list li { padding: 0.625rem 0.75rem; border: 1px solid #e5e7eb; border-radius: 0.5rem; background: #f9fafb; }
        .message-history-list .field-hint { margin-top: 0.25rem; }
        html.dark .message-history-list li { border-color: #2a3158; background: #131833; }

        .broadcast-mode-toggle { display: flex; gap: 0.5rem; }
        .broadcast-mode-btn { flex: 1; padding: 0.5rem 0.75rem; font-size: 0.8125rem; font-weight: 600; border-radius: 0.5rem; border: 1.5px solid #d1d5db; background: #fff; color: #374151; cursor: pointer; transition: all 0.15s; }
        .broadcast-mode-btn:hover { border-color: #9ca3af; }
        .broadcast-mode-btn.active { border-color: #6366f1; background: #eef2ff; color: #4f46e5; }
        .broadcast-select-all-link { background: none; border: none; padding: 0; font-size: 0.75rem; font-weight: 600; color: #6366f1; cursor: pointer; }
        .broadcast-select-all-link:hover { text-decoration: underline; }
        .broadcast-partner-list { max-height: 220px; overflow-y: auto; border: 1.5px solid #d1d5db; border-radius: 0.5rem; }
        .broadcast-partner-row { display: flex; align-items: center; gap: 0.625rem; padding: 0.5rem 0.75rem; border-bottom: 1px solid #f3f4f6; cursor: pointer; }
        .broadcast-partner-row:last-child { border-bottom: none; }
        .broadcast-partner-row:hover { background: #f9fafb; }
        html.dark .broadcast-mode-btn { background: #0c1226; border-color: #2a3158; color: #cbd5e1; }
        html.dark .broadcast-mode-btn:hover { border-color: #384070; }
        html.dark .broadcast-mode-btn.active { border-color: #818cf8; background: rgba(99,102,241,0.15); color: #a5b4fc; }
        html.dark .broadcast-partner-list { border-color: #2a3158; }
        html.dark .broadcast-partner-row { border-bottom-color: #1a2140; }
        html.dark .broadcast-partner-row:hover { background: #1a2140; }

        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 1rem; }
        .partner-modal, .ledger-modal { background: #fff; border-radius: 1rem; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); width: 100%; max-height: 90vh; overflow-y: auto; }
        .partner-modal { max-width: 640px; }
        .ledger-modal { max-width: 780px; }
        .partner-modal__header { display: flex; justify-content: space-between; align-items: flex-start; padding: 1.25rem 1.5rem; border-bottom: 1px solid #e5e7eb; background: #f9fafb; }
        .partner-modal__header h2 { font-size: 1.125rem; font-weight: 700; color: #111827; margin: 0; }
        .partner-modal__subtitle { font-size: 0.8125rem; color: #6b7280; margin: 0.25rem 0 0; }
        .partner-modal__close { background: transparent; border: none; padding: 0.375rem; border-radius: 0.5rem; color: #9ca3af; cursor: pointer; transition: all 0.15s; }
        .partner-modal__close:hover { background: #e5e7eb; color: #374151; }
        .partner-modal__body, .ledger-modal__body { padding: 1.5rem; }
        .partner-modal__error { display: flex; align-items: center; gap: 0.5rem; background: #fef2f2; color: #991b1b; padding: 0.75rem 1rem; border-radius: 0.5rem; margin-bottom: 1rem; font-size: 0.875rem; border: 1px solid #fecaca; }
        .partner-modal__section-title { font-size: 0.75rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin: 1.25rem 0 0.75rem; padding-top: 0.75rem; border-top: 1px solid #f3f4f6; }
        .partner-modal__grid--2col { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
        .partner-modal__grid--3col { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 1rem; }
        @media (max-width: 640px) { .partner-modal__grid--2col, .partner-modal__grid--3col { grid-template-columns: 1fr; } }
        .partner-modal__field { margin-bottom: 1rem; }
        .partner-modal__field label { display: block; font-size: 0.8125rem; font-weight: 600; color: #374151; margin-bottom: 0.375rem; }
        .partner-modal__field .required { color: #ef4444; }
        .partner-modal__field input, .partner-modal__field textarea, .partner-modal__field select { width: 100%; padding: 0.5rem 0.75rem; font-size: 0.875rem; border: 1.5px solid #d1d5db; border-radius: 0.5rem; background: #fff; transition: all 0.15s; color: #111827; }
        .partner-modal__field input:focus, .partner-modal__field textarea:focus, .partner-modal__field select:focus { outline: none; border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }
        .partner-modal__field input.field-disabled { background: #f3f4f6; color: #9ca3af; cursor: not-allowed; }
        .partner-modal__field textarea { resize: vertical; min-height: 60px; }
        .field-hint { display: block; font-size: 0.6875rem; color: #9ca3af; margin-top: 0.25rem; }
        .partner-password-field { position: relative; }
        .partner-password-field input { padding-right: 2.25rem !important; }
        .partner-password-toggle { position: absolute; top: 50%; right: 0.5rem; transform: translateY(-50%); background: none; border: none; padding: 0.25rem; display: flex; align-items: center; justify-content: center; color: #9ca3af; cursor: pointer; }
        .partner-password-toggle:hover { color: #6366f1; }

        .input-with-suffix { display: flex; align-items: center; gap: 0; }
        .input-with-suffix input { flex: 1; border-top-right-radius: 0; border-bottom-right-radius: 0; }
        .input-suffix { padding: 0.5rem 0.75rem; font-size: 0.875rem; font-weight: 600; color: #6b7280; background: #f9fafb; border: 1.5px solid #d1d5db; border-left: none; border-top-right-radius: 0.5rem; border-bottom-right-radius: 0.5rem; }

        .toggle-label { display: flex !important; align-items: center; gap: 0.5rem; font-size: 0.875rem; color: #374151; cursor: pointer; padding: 0.75rem 1rem; border-radius: 0.5rem; border: 1.5px solid #e5e7eb; transition: all 0.15s; }
        .toggle-label:hover { background: #f9fafb; border-color: #d1d5db; }
        .toggle-label input[type="checkbox"] { width: 1rem; height: 1rem; accent-color: #6366f1; }

        .partner-modal__actions { display: flex; justify-content: flex-end; gap: 0.75rem; padding-top: 1.25rem; border-top: 1px solid #e5e7eb; margin-top: 1rem; }

        .ledger-totals { display: flex; gap: 1rem; margin-bottom: 1.25rem; }
        .ledger-total-card { flex: 1; padding: 0.875rem 1rem; border-radius: 0.75rem; background: #f9fafb; border: 1px solid #e5e7eb; display: flex; flex-direction: column; gap: 0.25rem; }
        .ledger-total-label { font-size: 0.75rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; }
        .ledger-total-value { font-size: 1.25rem; font-weight: 700; }

        /* ===== Dark theme ===== */
        html.dark .header-left h1 { color: #f1f5f9; }
        html.dark .header-left p { color: #8ea1b8; }
        html.dark .empty-state h3 { color: #cbd5e1; }
        html.dark .empty-state p { color: #8ea1b8; }
        html.dark .partners-table th { background: #131833; color: #aebdce; border-bottom: 2px solid #2a3158; }
        html.dark .partners-table td { color: #cbd5e1; border-bottom: 1px solid #1a2140; }
        html.dark .partners-table tr:hover { background: #243060; }
        html.dark .partner-name { color: #f1f5f9; }
        html.dark .partner-sub { color: #8ea1b8; }
        html.dark .partner-token-badge { background: rgba(139, 92, 246, 0.18); color: #a5b4fc; }
        html.dark .recurrence-badge { background: #1a2140; color: #aebdce; }
        html.dark .recurrence-badge--autopay { background: rgba(245, 158, 11, 0.18); color: #fbbf24; }
        html.dark .status-active { background: rgba(34, 197, 94, 0.18); color: #4ade80; }
        html.dark .status-inactive { background: #1a2140; color: #aebdce; }
        html.dark .status-expired { background: rgba(239, 68, 68, 0.18); color: #f87171; }
        html.dark .status-scheduled { background: rgba(245, 158, 11, 0.18); color: #fbbf24; }
        html.dark .btn-outline { border-color: #2a3158; color: #cbd5e1; }
        html.dark .btn-outline:hover:not(:disabled) { background: #151c38; border-color: #384070; }
        html.dark .partner-modal, html.dark .ledger-modal { background: #161c36; border: 1px solid #2a3158; box-shadow: none; }
        html.dark .partner-modal__header { background: #131833; border-bottom-color: #2a3158; }
        html.dark .partner-modal__header h2 { color: #f1f5f9; }
        html.dark .partner-modal__subtitle { color: #8ea1b8; }
        html.dark .partner-modal__close { color: #8ea1b8; }
        html.dark .partner-modal__close:hover { background: #1a2140; color: #f1f5f9; }
        html.dark .partner-modal__error { background: rgba(239, 68, 68, 0.15); color: #fca5a5; border-color: rgba(248, 113, 113, 0.4); }
        html.dark .partner-modal__section-title { color: #8ea1b8; border-top-color: #2a3158; }
        html.dark .partner-modal__field label { color: #cbd5e1; }
        html.dark .partner-modal__field input, html.dark .partner-modal__field textarea, html.dark .partner-modal__field select { background: #0c1226; border-color: #2a3158; color: #e5e7eb; }
        html.dark .partner-modal__field input.field-disabled { background: #131833; color: #64748b; }
        html.dark .field-hint { color: #8ea1b8; }
        html.dark .partner-password-toggle { color: #64748b; }
        html.dark .partner-password-toggle:hover { color: #818cf8; }
        html.dark .input-suffix { background: #131833; border-color: #2a3158; color: #aebdce; }
        html.dark .toggle-label { color: #cbd5e1; border-color: #2a3158; }
        html.dark .toggle-label:hover { background: #151c38; border-color: #384070; }
        html.dark .partner-modal__actions { border-top-color: #2a3158; }
        html.dark .ledger-total-card { background: #131833; border-color: #2a3158; }
        html.dark .ledger-total-label { color: #8ea1b8; }
        html.dark .note-text { color: #e5e7eb; }
        html.dark .note-text--error { color: #fca5a5; }
      `}</style>
    </div>
  );
};

export default ChannelPartnersList;
